<template>
  <div>
    <!-- 单条折线图 -->
    <div v-if="!enableCompare" ref="singleChartRef" style="height: 400px; width: 100%;"></div>
    <!-- 双条折线图 -->
    <div v-else ref="compareChartRef" style="height: 400px; width: 100%;"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'TrendChart',
  props: {
    reportType: String,
    chartType: String,
    trendData: Object,
    compareTrendData: Object,
    enableCompare: Boolean,
    selectedTime: String,
    compareTime: String
  },
  data() {
    return {
      singleChart: null,
      compareChart: null
    }
  },
  watch: {
    trendData: {
      deep: true,
      handler() {
        this.updateCharts()
      }
    },
    compareTrendData: {
      deep: true,
      handler() {
        this.updateCharts()
      }
    },
    chartType() {
      this.updateCharts()
    },
    enableCompare() {
      this.$nextTick(() => {
        setTimeout(this.initCharts, 50)
      })
    }
  },
  mounted() {
    this.$nextTick(() => {
      setTimeout(this.initCharts, 50)
    })
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    this.disposeCharts()
    window.removeEventListener('resize', this.resizeCharts)
  },
  methods: {
    initCharts() {
      this.disposeCharts()
      if (this.enableCompare) {
        const dom = this.$refs.compareChartRef
        if (!dom) return console.warn('⚠️ compareChartRef 未挂载')
        this.compareChart = echarts.init(dom)
        this.updateCompareChart()
      } else {
        const dom = this.$refs.singleChartRef
        if (!dom) return console.warn('⚠️ singleChartRef 未挂载')
        this.singleChart = echarts.init(dom)
        this.updateSingleChart()
      }
    },
    resizeCharts() {
      if (this.singleChart) this.singleChart.resize()
      if (this.compareChart) this.compareChart.resize()
    },
    disposeCharts() {
      if (this.singleChart) {
        this.singleChart.dispose()
        this.singleChart = null
      }
      if (this.compareChart) {
        this.compareChart.dispose()
        this.compareChart = null
      }
    },
    updateCharts() {
      this.$nextTick(() => {
        if (this.enableCompare) {
          this.updateCompareChart()
        } else {
          this.updateSingleChart()
        }
      })
    },
    updateSingleChart() {
      if (!this.singleChart || !this.trendData) return

      const isAmount = this.chartType === 'amount'
      const currentData = isAmount ? this.trendData.amountData : this.trendData.salesData
      const xAxisLabels = this.trendData.xaxis || []

      this.singleChart.clear()

      const option = {
        tooltip: {
          trigger: 'axis',
          formatter(params) {
            if (!params || !params.length) return ''
            let res = params[0].name + '<br/>'
            params.forEach(item => {
              if (item && item.seriesName && item.value != null) {
                res += `${item.marker} ${item.seriesName}: ${item.value}<br/>` // 显示原始值，不格式化
              }
            })
            return res
          }
        },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisLabels,
        },
        yAxis: {
          type: 'value',
          name: isAmount ? '销售额(元)' : '销售量(箱)',
          axisLabel: {
            formatter: value => this.formatAxisValue(value, isAmount)
          }
        },
        series: [
          {
            name: this.selectedTime,
            type: 'line',
            smooth: true,
            data: currentData,
            itemStyle: { color: '#409EFF' },
            lineStyle: { width: 3 },
            symbol: 'circle',
            symbolSize: 8,
            label: {
              show: true,
              position: 'top',
              formatter: params => this.formatValue(params.data, isAmount)
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(64, 158, 255, 0.4)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.1)' }
              ])
            }
          }
        ]
      }

      this.singleChart.setOption(option)
    },
    updateCompareChart() {
      if (!this.compareChart || !this.trendData || !this.compareTrendData) return

      const isAmount = this.chartType === 'amount'
      const currentData = isAmount ? this.trendData.amountData : this.trendData.salesData
      const compareData = isAmount ? this.compareTrendData.amountData : this.compareTrendData.salesData
      const xAxisLabels = this.trendData.xaxis || []

      if (!xAxisLabels.length || !(currentData && currentData.length)) {
        console.warn('📭 没有折线图数据可展示（对比）')
        return
      }

      this.compareChart.clear()

      const option = {
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            if (!params || !params.length) return ''
            let result = `${params[0].name}<br/>`
            params.forEach(p => {
              result += `${p.marker} ${p.seriesName}: ${p.value != null ? p.value : 'N/A'}<br/>` // 显示原始值，不格式化
            })
            const currentVal = params[0].value
            const compareVal = (params[1] && params[1].value)
            if (currentVal != null && compareVal != null) {
              result += this.calcGrowthRate(currentVal, compareVal)
            }
            return result
          }
        },
        legend: {
          data: [this.selectedTime, this.compareTime]
        },
        grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xAxisLabels
        },
        yAxis: {
          type: 'value',
          name: isAmount ? '销售额(元)' : '销售量(箱)',
          axisLabel: {
            formatter: value => this.formatAxisValue(value, isAmount)
          }
        },
        series: [
          {
            name: this.selectedTime,
            type: 'line',
            smooth: true,
            data: currentData,
            itemStyle: { color: '#409EFF' },
            lineStyle: { width: 3 },
            symbol: 'circle',
            symbolSize: 8,
            label: {
              show: true,
              position: 'top',
              formatter: params => this.formatValue(params.data, isAmount)
            }
          },
          {
            name: this.compareTime,
            type: 'line',
            smooth: true,
            data: compareData,
            itemStyle: { color: '#67C23A' },
            lineStyle: { width: 3, type: 'dashed' },
            symbol: 'circle',
            symbolSize: 8,
            label: {
              show: true,
              position: 'top',
              formatter: params => this.formatValue(params.data, isAmount)
            }
          }
        ]
      }

      this.compareChart.setOption(option)
    },
    formatValue(value, isAmount) {
      if (value == null) return 'N/A'
      if (isAmount) {
        return value < 10000
            ? `¥${value.toLocaleString()}`
            : `¥${(value / 10000).toFixed(1)}万`
      } else {
        return value < 1000
            ? `${value.toLocaleString()}箱`
            : `${(value / 1000).toFixed(1)}千箱`
      }
    },
    formatAxisValue(value, isAmount) {
      if (isAmount) {
        return value < 10000
            ? value.toLocaleString()
            : `${(value / 10000).toFixed(1)}万`
      } else {
        return value < 1000
            ? value.toLocaleString()
            : `${(value / 1000).toFixed(1)}千`
      }
    },
    calcGrowthRate(current, compare) {
      if (compare === 0) {
        return current === 0
            ? '同比: 0%'
            : '同比: 新增'
      }
      const rate = ((current - compare) / compare * 100).toFixed(2)
      const trend = rate >= 0 ? '↑' : '↓'
      const color = rate >= 0 ? '#67C23A' : '#F56C6C'
      return `<span style="color:${color}">同比: ${trend}${Math.abs(rate)}%</span>`
    }
  }
}
</script>



// WEBPACK FOOTER //
// TrendChart.vue?24896388